/**
 * @module hooks/useProlificParams
 * @description Reads Prolific URL parameters (PROLIFIC_PID, STUDY_ID, SESSION_ID)
 * from the browser's query string. Also supports test mode via ?user=xxx&cond=xxx.
 * Returns stable references.
 *
 * Prolific sends participants with: ?PROLIFIC_PID=xxx&STUDY_ID=xxx&SESSION_ID=xxx
 * Test/debug mode uses:             ?user=xxx&cond=xxx
 */

import { useMemo } from "react";
import { isDebugPrefillParticipantCode } from "../utils/debugPrefill";

/**
 * Extract Prolific parameters and test-mode params from the current URL.
 * @returns {{
 *   prolificPid: string|null,
 *   studyId: string|null,
 *   sessionId: string|null,
 *   testUser: string|null,
 *   testCond: string|null,
 *   isTestMode: boolean,
 * }}
 */
export const useProlificParams = () => {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);

    const prolificPid = params.get("PROLIFIC_PID") || null;
    const studyId = params.get("STUDY_ID") || null;
    const sessionId = params.get("SESSION_ID") || null;

    // Test mode: ?user=xxx&cond=xxx. DEBUG002 can also be opened as ?user=DEBUG002.
    const testUser = params.get("user") || null;
    const testCond = params.get("cond") || null;
    const isTestMode = !!(testUser && (testCond || isDebugPrefillParticipantCode(testUser)));

    return { prolificPid, studyId, sessionId, testUser, testCond, isTestMode };
  }, []); // Only computed once on mount – URL params don't change mid-session
};
